import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Button as AntdButton, Skeleton } from 'antd';
import {
  ArrowLeft2,
  ArrowRight2,
  CloseCircle,
  TickCircle,
  RepeateMusic,
} from 'iconsax-react';
import Button from '../../../components/atoms/Button';
import TitleText from '../../../components/atoms/TitleText';
import {
  PromotionRequest,
  PromotionDetailsResponse,
  PromotionActions,
} from '../../../types/promotions';
import { useGetPromotionDetailsQuery } from '../../../store/services/promotionsService';
import PromotionReturnModal from './modals/PromotionReturnModal';
import PromotionRejectModal from './modals/PromotionRejectModal';
import PromotionApproveModal from './modals/PromotionApproveModal';
import FlagComponent from 'react-world-flags';
import Helpers from '../../../lib/helpers';
import ActivityLogsList from '@/components/AdminLogs/IndividualLog/ActivityLogsList';

const Flag = FlagComponent as React.ComponentType<{
  code?: string;
  width?: string;
  height?: string;
  className?: string;
}>;
interface ApplicationDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  item: PromotionRequest | null;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  onReturn?: () => void;
  onReject?: () => void;
  onApprove?: () => void;
}

export default function ApplicationDetailsModal({
  isOpen,
  onClose,
  item,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  onReturn,
  onReject,
  onApprove,
}: ApplicationDetailsDrawerProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const [isModalReturnOpen, setIsModalReturnOpen] = useState(false);
  const [isModalRejectOpen, setIsModalRejectOpen] = useState(false);
  const [isModalApproveOpen, setIsModalApproveOpen] = useState(false);

  const { data: detailsData, isLoading: isLoadingDetails } =
    useGetPromotionDetailsQuery(item?.id as string, {
      skip: !item?.id || !isOpen,
      refetchOnMountOrArgChange: true,
    });

  if (!item) return null;

  const currentDetails: PromotionDetailsResponse =
    (detailsData as any)?.payload || (detailsData as any) || item;

  const actions: PromotionActions | null | undefined =
    currentDetails?.actions ??
    (detailsData as any)?.payload?.actions ??
    (detailsData as any)?.actions ??
    item?.actions;

  const canReturn = Boolean(actions?.canReturn);
  const canReject = Boolean(actions?.canReject);
  const canApprove = Boolean(actions?.canApprove);
  const hasAnyAction = canReturn || canReject || canApprove;

  const rawStatus = String(currentDetails.statusCode || '').toUpperCase();

  const statusMap: Record<
    string,
    { label: string; color: string; bg: string }
  > = {
    APPROVED: {
      label: t('home.promo-status-approved', 'Approved'),
      color: '#22C55E',
      bg: '#F0FDF4',
    },
    REJECTED: {
      label: t('home.promo-status-rejected', 'Rejected'),
      color: '#EF4444',
      bg: '#FEF2F2',
    },
    PENDING: {
      label: t('home.promo-status-pending', 'Pending'),
      color: '#F59E0B',
      bg: '#FFFBEB',
    },
    PENDING_REVIEW: {
      label: t('home.promo-status-pending', 'Pending Review'),
      color: '#F59E0B',
      bg: '#FFFBEB',
    },
    WAITING_ACCEPTANCE: {
      label: t('home.promo-status-pending', 'Pending'),
      color: '#F59E0B',
      bg: '#FFFBEB',
    },
    ONGOING: {
      label: t('home.promo-status-ongoing', 'Ongoing'),
      color: '#06B6D4',
      bg: '#ECFEFF',
    },
    RETURNED: {
      label: t('home.promo-action-return', 'Returned'),
      color: '#F59E0B',
      bg: '#FFFBEB',
    },
    CANCELED: {
      label: currentDetails.statusName || 'Canceled',
      color: '#6B7280',
      bg: '#F9FAFB',
    },
  };

  const status = statusMap[rawStatus] || {
    label: currentDetails.statusName || '-',
    color: '#6B7280',
    bg: '#F9FAFB',
  };

  const typeLabels: Record<string, string> = {
    TOP_PRODUCTS: t('home.top-products', 'Top products'),
    HERO_BANNER: t('home.hero-banners', 'Hero banners'),
    FLASH_DEALS: t('home.flash-deals', 'Flash deals'),
  };
  const rawType = String(currentDetails.promotionTypeCode || '').toUpperCase();
  const promoType = typeLabels[rawType] || rawType || '-';

  const scopeLabels: Record<string, string> = {
    MAIN_MARKETPLACE: t('home.main-marketplace', 'Main Marketplace'),
    CATEGORY_MARKETPLACE: t(
      'home.category-marketplace',
      'Category Marketplace'
    ),
  };
  const rawScope = String(
    currentDetails.marketplaceScopeCode || ''
  ).toUpperCase();
  const placement = scopeLabels[rawScope] || '-';

  const product = currentDetails.productName || '-';

  const durationStr = (() => {
    if (
      currentDetails.duration !== undefined &&
      currentDetails.duration !== null
    ) {
      const isHour = currentDetails.durationUnitCode?.toUpperCase() === 'HOUR';
      const unit = isHour
        ? t('home.duration-unit-hours', 'hour')
        : t('home.duration-unit-days', 'days');
      return `${currentDetails.duration} ${unit}`;
    }
    return '-';
  })();

  const priceStr = (() => {
    const val = currentDetails.price ?? currentDetails.totalAmount;
    if (val !== undefined && val !== null) {
      return `${val} ${t('SAR')}`;
    }
    return '-';
  })();

  const detailRows = [
    { label: t('home.promo-detail-promotion-type'), value: promoType },
    { label: t('home.promo-detail-placement'), value: placement },
    { label: t('home.promo-product'), value: product },
    { label: t('home.promo-duration'), value: durationStr },
    { label: t('home.promo-price'), value: priceStr },
  ];

  const sellerObj =
    typeof currentDetails.seller === 'object' && currentDetails.seller !== null
      ? currentDetails.seller
      : null;

  const sellerName =
    sellerObj?.companyName ||
    (sellerObj?.firstName
      ? `${sellerObj.firstName} ${sellerObj.lastName || ''}`.trim()
      : '') ||
    currentDetails.seller?.name ||
    currentDetails.seller?.userName ||
    (typeof currentDetails.seller === 'string' ? currentDetails.seller : '') ||
    (currentDetails.seller?.firstName
      ? `${currentDetails.seller.firstName} ${currentDetails.seller.lastName || ''}`.trim()
      : '') ||
    currentDetails.seller?.companyName ||
    '';

  const sellerEmail = sellerObj?.email || currentDetails.seller?.email || '';

  const sellerPhone =
    (sellerObj?.phoneCode && sellerObj?.phoneNumber
      ? `${sellerObj.phoneCode} ${sellerObj.phoneNumber}`
      : sellerObj?.phoneNumber) || '';

  const sellerPhoto = sellerObj?.photoUrl;

  const previewImage = currentDetails.documentUrl;

  const hasSeller = Boolean(sellerName || sellerEmail || sellerPhone);

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      placement={isRtl ? 'left' : 'right'}
      width={700}
      closable={false}
      className="!overflow-hidden"
      title={
        <div
          className="header-title flex items-center justify-between gap-2 pt-4"
          data-test-id="promo-details-header"
        >
          <div className="flex items-center gap-2">
            <TitleText
              text={t('home.promo-application-details')}
              className="!font-bold !leading-6"
            />
            <AntdButton
              className="text-text-secondary !text-sm"
              color="default"
              variant="text"
              icon={<ArrowLeft2 size={12} />}
              onClick={onPrevious}
              disabled={!hasPrevious}
              data-test="promo-details-prev-btn"
            >
              {t('home.prev')}
            </AntdButton>
            <AntdButton
              className="text-text-secondary !text-sm"
              color="default"
              variant="text"
              icon={<ArrowRight2 size={12} />}
              iconPosition="end"
              onClick={onNext}
              disabled={!hasNext}
              data-test="promo-details-next-btn"
            >
              {t('home.next')}
            </AntdButton>
          </div>
          <AntdButton
            type="text"
            variant="text"
            color="default"
            onClick={onClose}
            className="!p-1.5 !min-w-0 !h-auto rounded"
            icon={
              <CloseCircle
                size={20}
                variant="Bold"
                className="text-light-text-primary"
              />
            }
            data-test="promo-details-close-btn"
          />
        </div>
      }
      footer={
        hasAnyAction ? (
          <div
            className="footer-actions flex gap-3 justify-between"
            data-test-id="promo-details-footer"
          >
            <div className="flex gap-3">
              {canReturn && (
                <Button
                  variant="warning"
                  size="sm"
                  icon={<RepeateMusic size={16} color="#ffffff" />}
                  onClick={() => setIsModalReturnOpen(true)}
                  className="!rounded-lg"
                  data-test="promo-details-return-btn"
                >
                  {t('home.promo-action-return')}
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              {canReject && (
                <Button
                  variant="danger"
                  size="sm"
                  icon={<CloseCircle size={16} color="#ffffff" />}
                  onClick={() => setIsModalRejectOpen(true)}
                  className="!rounded-lg"
                  data-test="promo-details-reject-btn"
                >
                  {t('home.promo-action-reject')}
                </Button>
              )}
              {canApprove && (
                <Button
                  variant="success"
                  size="sm"
                  icon={<TickCircle size={16} color="#ffffff" />}
                  onClick={() => setIsModalApproveOpen(true)}
                  className="!rounded-lg"
                  data-test="promo-details-approve-btn"
                >
                  {t('home.promo-action-approve')}
                </Button>
              )}
            </div>
          </div>
        ) : null
      }
    >
      <div className="body">
        {isLoadingDetails ? (
          <div className="flex flex-col gap-6 py-2">
            <Skeleton active title={{ width: '50%' }} paragraph={{ rows: 6 }} />
            <Skeleton.Image className="!w-full !h-[200px] !rounded-xl" active />
            <Skeleton
              active
              avatar
              title={{ width: '40%' }}
              paragraph={{ rows: 2 }}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {/* Detail Rows */}
            <div className="flex flex-col divide-y divide-gray-100">
              {detailRows.map((row, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-3"
                >
                  <span className="text-[#637381] text-sm font-normal">
                    {row.label}
                  </span>
                  <span className="text-[#111928] text-sm font-bold">
                    {row.value}
                  </span>
                </div>
              ))}
              {/* Status row */}
              <div className="flex items-center justify-between py-3">
                <span className="text-[#637381] text-sm font-normal">
                  {t('home.status')}
                </span>
                <span
                  className="text-sm font-semibold px-2 py-0.5 rounded-md"
                  style={{
                    color: status.color,
                    backgroundColor: status.bg,
                  }}
                >
                  {status.label}
                </span>
              </div>
            </div>

            {/* Return Message Notice */}
            {/* {currentDetails.returnMessage && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5 flex flex-col gap-1 text-sm">
                <span className="font-semibold text-amber-900">
                  {t('home.promo-action-return', 'Return reason')}
                </span>
                <p className="text-amber-800 break-words m-0">
                  {currentDetails.returnMessage}
                </p>
              </div>
            )} */}

            {/* Preview Image */}
            {previewImage && (
              <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 w-full h-[200px]">
                <img
                  src={previewImage}
                  alt="Promotion preview"
                  className="w-full h-full object-fill"
                />
              </div>
            )}

            {/* Seller Information */}
            {hasSeller && (
              <div className="flex flex-col gap-3">
                <h4 className="text-[#111928] text-base font-bold">
                  {t('home.promo-seller-information')}
                </h4>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    {sellerPhoto ? (
                      <img
                        src={sellerPhoto}
                        alt={sellerName || 'Seller'}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                        {(sellerName || 'S')
                          .split(' ')
                          .map((n: string) => n[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-[#111928] text-sm font-semibold">
                        {sellerName || '-'}
                      </span>
                      {sellerEmail && (
                        <span className="text-[#637381] text-xs font-normal">
                          {sellerEmail}
                        </span>
                      )}
                    </div>
                  </div>
                  {sellerPhone && (
                    <div className="flex items-center gap-2">
                      <div
                        className="flex items-center gap-1.5 text-[#637381] text-xs font-medium"
                        dir="ltr"
                      >
                        <Flag
                          code={
                            (sellerObj?.phoneCode
                              ? Helpers.getCountryCodeFromCallingCode(
                                  sellerObj.phoneCode
                                )
                              : sellerObj?.address?.country?.code) || 'SA'
                          }
                          className="w-4 h-3 object-cover rounded-sm"
                        />
                        <span>{sellerPhone}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <ActivityLogsList id={item?.id} type="PROMOTION" />
          </div>
        )}
      </div>

      {isModalReturnOpen && item?.id && (
        <PromotionReturnModal
          isOpen={isModalReturnOpen}
          promotionId={item.id}
          onClose={(success) => {
            setIsModalReturnOpen(false);
            if (success) {
              onReturn?.();
              onClose();
            }
          }}
        />
      )}

      {isModalApproveOpen && item?.id && (
        <PromotionApproveModal
          isOpen={isModalApproveOpen}
          promotionId={item.id}
          onClose={(success) => {
            setIsModalApproveOpen(false);
            if (success) {
              onApprove?.();
              onClose();
            }
          }}
        />
      )}

      {isModalRejectOpen && item?.id && (
        <PromotionRejectModal
          isOpen={isModalRejectOpen}
          promotionId={item.id}
          onClose={(success) => {
            setIsModalRejectOpen(false);
            if (success) {
              onReject?.();
              onClose();
            }
          }}
        />
      )}
    </Drawer>
  );
}
